import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  InvoiceConfiguration,
  InvoicePositionModel,
} from '../../../../core/models/finance/financial-record.model';
import {
  BudgetOptions,
  InvoiceType,
} from '../../../../core/constants-repository/project-calculation.constants';
import { assert } from '../../../../core/assert/assert';
import { TimeBlockProjectModel } from '../../../../core/models/timeblock/time-block.model';
import { ProjectMonetaryCalculationService } from '../../../project/project-details/project-progress/services/project-monetary-calculation.service';
import { BudgetEntryService } from '../../../project/project-details/project-edit/manage-project/project-calculation-form/services/budget-entry.service';
import { GroupingOptions } from '../grouping-constants';
import { ProjectDataService } from '../../../project/project-details/state-and-data-handling/project-data.service';
import { TimeBlockFee } from '../../../project/project-details/project-edit/edit-project.types';
import { InvoiceTimespan } from '../invoice-types';
import { MathUtil } from '../../../../features/calendar/util/math';
import { TimeBlockWithTask } from '../../../../shared/data-types/shared-time-block-types';
import { SharedTaskHandlerService } from '../../../../core/services/features/shared-task-handling/shared-task-handler.service';
import { TimeBlockFeeCalculationService } from '../../../project/project-details/project-progress/services/time-block-fee-calculation.service';

interface TimeBlockGrouping {
  key: number;
  description: string;
}

@Injectable()
export class InvoicePositionHandlerService {
  private readonly timeBlockFeeCalculationService = inject(TimeBlockFeeCalculationService);
  private readonly projectMonetaryCalculationService = inject(ProjectMonetaryCalculationService);
  private readonly budgetEntryService = inject(BudgetEntryService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly sharedTaskHandlerService = inject(SharedTaskHandlerService);

  public getInvoicePositions(
    invoiceConfig: InvoiceConfiguration,
  ): Observable<InvoicePositionModel[]> {
    switch (invoiceConfig.expenditureType) {
      case BudgetOptions.TotalProjectMonetaryBudget:
        return this.createTotalProjectMonetaryPositions(invoiceConfig);

      case BudgetOptions.MonetaryPerTask:
        return this.createMonetaryPositionsPerTask(invoiceConfig);

      case InvoiceType.TimeAndMaterial:
        return this.createTimeAndMaterialPositions(invoiceConfig);

      default:
        return of([]);
    }
  }

  public sumInvoicePositions(invoicePositions: InvoicePositionModel[]): number {
    return MathUtil.roundTwoDecimals(
      invoicePositions.reduce((total, position) => total + +position.price, 0),
    );
  }

  public calculateTaxAmount(invoicePositions: InvoicePositionModel[], taxRate: number): number {
    return MathUtil.roundTwoDecimals(this.sumInvoicePositions(invoicePositions) * taxRate);
  }

  private createTotalProjectMonetaryPositions(
    invoiceConfig: InvoiceConfiguration,
  ): Observable<InvoicePositionModel[]> {
    assert(invoiceConfig.project!.projectBudgetEntries.length === 1);

    const projectBudgetEntry = invoiceConfig.project!.projectBudgetEntries[0];
    const invoicePosition = new InvoicePositionModel();
    invoicePosition.id = 1;
    invoicePosition.description = invoiceConfig.project!.name;
    invoicePosition.price = projectBudgetEntry.monetary!;
    invoicePosition.timeBlockIds = [];

    return of([invoicePosition]);
  }

  private createMonetaryPositionsPerTask(
    invoiceConfig: InvoiceConfiguration,
  ): Observable<InvoicePositionModel[]> {
    const tasks = this.projectDataService.getTasks(invoiceConfig.project!, false, false);

    const invoicePositions: InvoicePositionModel[] = [];
    tasks.forEach((task, index) => {
      const projectBudgetEntry = this.budgetEntryService.getEntryForTask(
        invoiceConfig.project!,
        task.id!,
      );

      const entry = new InvoicePositionModel();
      entry.id = index + 1;
      entry.description = task.name;
      entry.price = projectBudgetEntry.monetary!;
      entry.timeBlockIds = [];
      invoicePositions.push(entry);
    });

    return of(invoicePositions);
  }

  private createTimeAndMaterialPositions(
    invoiceConfig: InvoiceConfiguration,
  ): Observable<InvoicePositionModel[]> {
    const since = this.determineSinceDate(invoiceConfig);
    const project = invoiceConfig.project!;

    // todo add filter to the fetchTimeBlocksForTasks method to only fetch time blocks since the given date
    // i.e. call it: return this.timeBlockHttpService.fetchTimeBlocksForTasks(taskIds, since null).pipe(
    return this.sharedTaskHandlerService
      .fetchTimeBlocksWithTasks(project, false, false, false, true)
      .pipe(map((blocks) => this.processTimeBlocks(invoiceConfig, blocks, since)));
  }

  private determineSinceDate(invoiceConfig: InvoiceConfiguration): Date | null {
    return invoiceConfig.tmHours === InvoiceTimespan.TmSince ? invoiceConfig.invoicedSince : null;
  }

  private processTimeBlocks(
    invoiceConfig: InvoiceConfiguration,
    timeBlockWithTasks: readonly TimeBlockWithTask[],
    since: Date | null,
  ): InvoicePositionModel[] {
    const timeBlockFees = this.timeBlockFeeCalculationService.timeBlockFeesOfProject(
      invoiceConfig.project!,
      timeBlockWithTasks,
      true,
      { start: since, end: null },
    );

    const groupedTimeBlockFees = this.groupTimeBlockFees(timeBlockFees, invoiceConfig.tmGrouping);

    return this.createInvoicePositionsFromGroups(groupedTimeBlockFees, invoiceConfig.tmGrouping);
  }

  private groupTimeBlockFees(
    timeBlockFees: readonly TimeBlockFee[],
    tmGrouping: string,
  ): Map<number, TimeBlockFee[]> {
    const groupedTimeBlocks = new Map<number, TimeBlockFee[]>();

    timeBlockFees.forEach((timeBlockFee) => {
      const grouping = this.determineGrouping(timeBlockFee, tmGrouping);

      if (!groupedTimeBlocks.has(grouping.key)) {
        groupedTimeBlocks.set(grouping.key, []);
      }
      groupedTimeBlocks.get(grouping.key)!.push(timeBlockFee);
    });

    return groupedTimeBlocks;
  }

  private determineGrouping(timeBlockFee: TimeBlockFee, tmGrouping: string): TimeBlockGrouping {
    const timeBlockModel = timeBlockFee.timeBlockWithTask.timeBlock
      .timeBlockModel as TimeBlockProjectModel;

    switch (tmGrouping) {
      case GroupingOptions.ByTask:
        return {
          key: +timeBlockModel.task.id!,
          description: timeBlockModel.task.name,
        };

      case GroupingOptions.ByMember:
        return {
          key: timeBlockModel.userId!,
          description: timeBlockModel.project.name,
        };

      case GroupingOptions.ByTimeBlock:
      default:
        return {
          key: timeBlockModel.id,
          description: timeBlockModel.description,
        };
    }
  }

  private createInvoicePositionsFromGroups(
    groupedTimeBlockFees: Map<number, TimeBlockFee[]>,
    tmGrouping: string,
  ): InvoicePositionModel[] {
    const invoicePositions: InvoicePositionModel[] = [];
    let counter = 1;

    for (const [, timeBlockFees] of groupedTimeBlockFees) {
      const invoicePosition = new InvoicePositionModel();
      invoicePosition.id = counter;
      const grouping = this.determineGrouping(timeBlockFees[0], tmGrouping);
      invoicePosition.description = grouping.description;
      invoicePosition.price = this.projectMonetaryCalculationService.calculateFeeSum(timeBlockFees);
      invoicePosition.timeBlockIds = timeBlockFees.map(
        (timeBlockFee) => timeBlockFee.timeBlockWithTask.timeBlock.id,
      );
      invoicePositions.push(invoicePosition);
      counter++;
    }

    return invoicePositions;
  }
}
